/**
 * Conference Live Dashboard — moderator command surface for an active
 * conference. Route: `/portal/conferences/:roomId/live`.
 *
 * Styling: standard Ivrello portal shell. Uses PageHeader + shadcn
 * primitives (Card / Button / Badge / Switch / Dialog / DataTable) so
 * the page sits cleanly next to /voicemails, /conference-rooms, and
 * /extensions. No bespoke CSS — all visual presentation is Tailwind
 * utility classes through shadcn variants.
 *
 * Layout:
 *  - PageHeader with the room name, live status pill, and the
 *    "End conference" destructive action (gated by confirm dialog).
 *  - Two-column main area on lg+ (stack on mobile):
 *      Left  (60%): Participants DataTable with mute / kick row actions.
 *      Right (40%): Controls Card (Lock + Q&A switches, Recording, Mute
 *                   all, Play prompt, Dial out) and the Q&A queue Card
 *                   (only visible when qaMode=true).
 *  - Full-width Event log card under the two-column area — last 50
 *    events with colored Badge per kind. Auto-scrolls to latest unless
 *    the operator has scrolled away.
 *
 * Data flow (unchanged from the previous design):
 *  - GET /api/conferences/:id/live (TanStack Query) seeds initial state.
 *  - WS /api/conferences/:id/stream pushes a `state_snapshot` then
 *    incremental `LiveEvent` patches. We fold each event via
 *    `applyEvent()` into a useState() cache.
 *  - Mutations call `conferenceLive.*` REST verbs; every onSettled
 *    invalidates the snapshot query so the REST refetch is a safety net
 *    behind the WS feed.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  useQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  Mic,
  MicOff,
  UserX,
  Hand,
  PhoneOff,
  PhoneOutgoing,
  PlayCircle,
  Lock,
  Volume2,
  VolumeX,
  ArrowUp,
  ArrowDown,
  X,
  Radio,
  Wifi,
  WifiOff,
  ChevronLeft,
  Disc,
} from 'lucide-react';

import {
  conferenceLive,
  type LiveConferenceState,
  type LiveEvent,
  type LiveParticipant,
  type QaQueueEntry,
} from '@/api/conferenceLive';
import { conferenceRooms } from '@/api/conferenceRooms';
import { extensions } from '@/api/extensions';
import { contacts } from '@/api/contacts';
import { useAuth } from '@/lib/auth-store';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { RecordingPicker } from '@/components/ivr/RecordingPicker';

// ─────────────────────────────────────────────────────────────────────
// Page entry point
// ─────────────────────────────────────────────────────────────────────

export function ConferenceLiveDashboard() {
  const { roomId = '' } = useParams<{ roomId: string }>();
  const accessToken = useAuth((s) => s.accessToken);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Room metadata — for the page title. Falls back gracefully if the
  // room row can't be loaded (e.g. permission).
  const { data: room } = useQuery({
    queryKey: ['conferenceRoom', roomId],
    queryFn: () => conferenceRooms.get(roomId),
    enabled: !!roomId,
    retry: false,
  });

  // Initial snapshot via REST. The WS overwrites this with its own
  // snapshot on connect, but the REST call gives us something to render
  // immediately even if the WS handshake is slow.
  const { data: initialState, isLoading, error } = useQuery({
    queryKey: ['conferenceLive', roomId],
    queryFn: () => conferenceLive.getLive(roomId),
    enabled: !!roomId,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const [state, setState] = useState<LiveConferenceState | null>(null);
  useEffect(() => {
    if (initialState) setState(initialState);
  }, [initialState]);

  // Event log — keep last 50 in memory. New ones are appended; the log
  // pane auto-scrolls to the bottom unless the user has scrolled away.
  const [log, setLog] = useState<LiveEvent[]>([]);

  // ── WebSocket lifecycle ────────────────────────────────────────────
  const [wsStatus, setWsStatus] = useState<'connecting' | 'open' | 'closed'>('connecting');
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!roomId) return;
    let cancelled = false;
    let retryHandle: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    function connect() {
      if (cancelled) return;
      const ws = conferenceLive.openStream(roomId, accessToken);
      wsRef.current = ws;
      setWsStatus('connecting');

      ws.onopen = () => {
        attempt = 0;
        setWsStatus('open');
      };
      ws.onerror = () => setWsStatus('closed');
      ws.onclose = () => {
        setWsStatus('closed');
        wsRef.current = null;
        if (cancelled) return;
        // Reconnect with simple backoff (1s, 2s, 4s ... max 10s).
        const delay = Math.min(1000 * 2 ** attempt, 10_000);
        attempt += 1;
        retryHandle = setTimeout(connect, delay);
      };
      ws.onmessage = (msg) => {
        try {
          const event = JSON.parse(String(msg.data)) as LiveEvent;
          setState((prev) => applyEvent(prev, event));
          setLog((prev) => {
            // Don't add the snapshot itself to the log — it's a bootstrap.
            if (event.kind === 'state_snapshot') return prev;
            const next = [...prev, event];
            return next.length > 50 ? next.slice(next.length - 50) : next;
          });
        } catch {
          // Drop malformed frames silently — they would have surfaced as
          // a WS error already.
        }
      };
    }

    connect();
    return () => {
      cancelled = true;
      if (retryHandle) clearTimeout(retryHandle);
      const ws = wsRef.current;
      if (ws && ws.readyState <= WebSocket.OPEN) ws.close();
      wsRef.current = null;
    };
  }, [roomId, accessToken]);

  // ── Mutations ─────────────────────────────────────────────────────
  //
  // Every mutation invalidates ['conferenceLive', roomId] on settle so
  // the snapshot REST refetches as a safety net behind the WS feed.
  // Success/failure toasts give the operator visible feedback that the
  // request hit the wire — important when the WS event hasn't echoed
  // back yet.
  const onSettled = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['conferenceLive', roomId] });
  }, [qc, roomId]);

  function toastFailure(verb: string) {
    return (err: unknown) =>
      toast({
        title: `Could not ${verb}`,
        description: err instanceof Error ? err.message : 'Unknown error.',
        variant: 'destructive',
      });
  }

  const muteOne = useMutation({
    mutationFn: (memberId: string) => conferenceLive.mute({ roomId, memberId }),
    onSettled,
    onError: toastFailure('mute'),
  });
  const unmuteOne = useMutation({
    mutationFn: (memberId: string) => conferenceLive.unmute({ roomId, memberId }),
    onSettled,
    onError: toastFailure('unmute'),
  });
  const kickOne = useMutation({
    mutationFn: (memberId: string) => conferenceLive.kick({ roomId, memberId }),
    onSettled,
    onError: toastFailure('remove participant'),
    onSuccess: () => toast({ title: 'Participant removed' }),
  });
  const muteAll = useMutation({
    mutationFn: () => conferenceLive.muteAll(roomId),
    onSettled,
    onError: toastFailure('mute all'),
    onSuccess: () => toast({ title: 'Muted everyone' }),
  });
  const unmuteAll = useMutation({
    mutationFn: () => conferenceLive.unmuteAll(roomId),
    onSettled,
    onError: toastFailure('unmute all'),
    onSuccess: () => toast({ title: 'Unmuted everyone' }),
  });
  const toggleLock = useMutation({
    mutationFn: (next: boolean) =>
      next ? conferenceLive.lock(roomId) : conferenceLive.unlock(roomId),
    onSettled,
    onError: toastFailure('change lock state'),
  });
  const toggleRec = useMutation({
    mutationFn: (action: 'start' | 'stop') =>
      conferenceLive.setRecording({ roomId, action }),
    onSettled,
    onError: toastFailure('change recording state'),
  });
  const endConf = useMutation({
    mutationFn: () => conferenceLive.end(roomId),
    onSettled,
    onError: toastFailure('end conference'),
    onSuccess: () => {
      toast({ title: 'Conference ended' });
      // Navigate back to the rooms list — the WS will close on its own.
      navigate('/conference-rooms');
    },
  });
  const callOnHand = useMutation({
    mutationFn: (memberId?: string) =>
      conferenceLive.callOnHand({ roomId, memberId }),
    onSettled,
    onError: toastFailure('call on participant'),
  });
  const reorderQueue = useMutation({
    mutationFn: (ordered: string[]) =>
      conferenceLive.reorderQueue({ roomId, ordered }),
    onSettled,
    onError: toastFailure('reorder queue'),
  });
  const lowerHand = useMutation({
    mutationFn: (memberId: string) =>
      conferenceLive.lowerHand({ roomId, memberId }),
    onSettled,
    onError: toastFailure('lower hand'),
  });
  const playPrompt = useMutation({
    mutationFn: (input: { recordingId?: string; path?: string }) =>
      conferenceLive.play({ roomId, ...input }),
    onSettled,
    onError: toastFailure('play prompt'),
    onSuccess: () => toast({ title: 'Playing prompt into room' }),
  });
  const dialOut = useMutation({
    mutationFn: ({ sofiaTarget, asModerator }: { sofiaTarget: string; asModerator: boolean }) =>
      conferenceLive.dial({ roomId, sofiaTarget, asModerator }),
    onSettled,
    onError: toastFailure('dial out'),
    onSuccess: () => toast({ title: 'Dial-out leg initiated' }),
  });

  // ── Confirm/dialog state ──────────────────────────────────────────
  const [endConfirm, setEndConfirm] = useState(false);
  const [kickConfirm, setKickConfirm] = useState<LiveParticipant | null>(null);
  const [playOpen, setPlayOpen] = useState(false);
  const [dialOpen, setDialOpen] = useState(false);

  // ── Loading / error / not-active ──────────────────────────────────
  if (!roomId) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-sm text-muted-foreground">
          No room id supplied.
        </CardContent>
      </Card>
    );
  }
  if (isLoading && !state) {
    return (
      <>
        <PageHeader
          eyebrow="Telephony"
          title="Live conference"
          description="Establishing link to room…"
        />
        <Card>
          <CardContent className="py-12 text-center text-sm text-muted-foreground">
            Loading…
          </CardContent>
        </Card>
      </>
    );
  }
  // No live state yet (room not active OR the snapshot fetch errored
  // with 404 'not_active'). Sam (2026-05-17): keep showing the full
  // dashboard layout — just stack an "inactive" banner on top and let
  // the participants / event-log sections render their empty states.
  // The control buttons stay visible but disabled (no live session to
  // act on); they re-enable the moment WS pushes a state_snapshot.
  const isInactive = !state;
  const liveState: LiveConferenceState = state ?? {
    roomId,
    sessionId: '',
    startedAt: new Date().toISOString(),
    locked: false,
    qaMode: false,
    currentSpeakerMemberId: null,
    recordingCustomerFacing: { active: false, path: null, startedAt: null },
    recordingBackend:         { active: false, path: null, startedAt: null },
    participants: [],
    qaQueue: [],
  };

  const activePill = isInactive
    ? <Badge variant="outline">Inactive</Badge>
    : liveState.participants.length > 0
      ? <Badge variant="default">Active</Badge>
      : <Badge variant="secondary">Idle</Badge>;

  return (
    <>
      <PageHeader
        eyebrow="Telephony"
        title={room?.name ?? 'Live conference'}
        description="Live moderator controls for the active conference. Mute, kick, lock the room, manage hand-raise queue, start recording, dial out, and play prompts in real-time."
        actions={
          <div className="flex items-center gap-2">
            {activePill}
            <Button asChild variant="outline" size="sm">
              <Link to="/conference-rooms">
                <ChevronLeft className="h-4 w-4 me-1.5" /> Rooms
              </Link>
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setEndConfirm(true)}
              disabled={endConf.isPending || isInactive}
            >
              <PhoneOff className="h-4 w-4 me-1.5" /> End conference
            </Button>
          </div>
        }
      />

      {isInactive && (
        <div
          className="mb-4 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          role="status"
          aria-live="polite"
          data-testid="conference-inactive-banner"
        >
          <div className="font-medium">This room is not currently active.</div>
          <div className="text-xs mt-0.5 text-amber-800">
            The dashboard will fill in automatically the moment the first caller joins
            — refresh not needed. Controls are disabled until the conference is live.
          </div>
        </div>
      )}

      {/* Live status strip — sits between PageHeader and the main grid.
          Mirrors how DataTable list-pages put a search bar there. */}
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          {wsStatus === 'open' ? (
            <Wifi className="h-3 w-3 text-emerald-600" aria-label="Connected" />
          ) : (
            <WifiOff className="h-3 w-3 text-amber-600" aria-label="Disconnected" />
          )}
          <span className="capitalize">{wsStatus}</span>
        </span>
        <span aria-hidden>·</span>
        <LiveDuration startedAt={liveState.startedAt} />
        <span aria-hidden>·</span>
        <span>
          {liveState.participants.length} on the line
          {' '}({liveState.participants.filter((p) => p.isModerator).length} mod)
        </span>
        {liveState.recordingCustomerFacing.active && (
          <>
            <span aria-hidden>·</span>
            <span className="inline-flex items-center gap-1 text-red-600 font-medium">
              <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
              Recording
            </span>
          </>
        )}
      </div>

      {/* Stack layout (Sam 2026-05-19): Controls (+ Q&A queue when on)
          on top, Participants below — full width each. Previously was a
          side-by-side 3/2 split which forced the operator to scan two
          columns at once. */}
      <div className="flex flex-col gap-4">
        <ControlsCard
          state={liveState}
          onToggleRec={(next) => toggleRec.mutate(next ? 'start' : 'stop')}
          onToggleLock={(next) => toggleLock.mutate(next)}
          onMuteAll={() => muteAll.mutate()}
          onUnmuteAll={() => unmuteAll.mutate()}
          onPlay={() => setPlayOpen(true)}
          onDial={() => setDialOpen(true)}
          recordingPending={toggleRec.isPending}
          lockPending={toggleLock.isPending}
          muteAllPending={muteAll.isPending}
          unmuteAllPending={unmuteAll.isPending}
        />
        {liveState.qaMode && (
          <QaQueueCard
            state={liveState}
            onCallOn={(memberId) => callOnHand.mutate(memberId)}
            onReorder={(ordered) => reorderQueue.mutate(ordered)}
            onLower={(memberId) => lowerHand.mutate(memberId)}
          />
        )}
        <ParticipantsCard
          state={liveState}
          onMute={(m) => muteOne.mutate(m.memberId)}
          onUnmute={(m) => unmuteOne.mutate(m.memberId)}
          onKick={(m) => setKickConfirm(m)}
        />
      </div>

      {/* ── Event log: full-width row ─────────────────────────── */}
      <div className="mt-4">
        <EventLogCard log={log} />
      </div>

      {/* ── Modals ────────────────────────────────────────────── */}
      <ConfirmDialog
        open={endConfirm}
        onOpenChange={setEndConfirm}
        title="End this conference?"
        description="Every leg hangs up. There is no undo."
        confirmLabel="End now"
        confirmVariant="destructive"
        pending={endConf.isPending}
        onConfirm={() => {
          endConf.mutate();
          setEndConfirm(false);
        }}
      />
      <ConfirmDialog
        open={!!kickConfirm}
        onOpenChange={(v) => { if (!v) setKickConfirm(null); }}
        title="Remove participant?"
        description={
          kickConfirm
            ? `Remove ${kickConfirm.displayName ?? kickConfirm.e164 ?? kickConfirm.memberId} from the room.`
            : ''
        }
        confirmLabel="Remove"
        confirmVariant="destructive"
        pending={kickOne.isPending}
        onConfirm={() => {
          if (kickConfirm) kickOne.mutate(kickConfirm.memberId);
          setKickConfirm(null);
        }}
      />
      <PlayPromptDialog
        open={playOpen}
        onOpenChange={setPlayOpen}
        pending={playPrompt.isPending}
        customerId={room?.customer_id ?? null}
        onPlay={(input) => {
          playPrompt.mutate(input);
          setPlayOpen(false);
        }}
      />
      <DialOutDialog
        open={dialOpen}
        onOpenChange={setDialOpen}
        pending={dialOut.isPending}
        customerId={room?.customer_id ?? null}
        onDial={(sofiaTarget, asModerator) => {
          dialOut.mutate({ sofiaTarget, asModerator });
          setDialOpen(false);
        }}
      />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Participants card (DataTable)
// ─────────────────────────────────────────────────────────────────────

interface ParticipantsCardProps {
  state: LiveConferenceState;
  onMute: (m: LiveParticipant) => void;
  onUnmute: (m: LiveParticipant) => void;
  onKick: (m: LiveParticipant) => void;
}

interface ParticipantTableRow extends LiveParticipant {
  // DataTable requires `id`; participants have memberId.
  id: string;
  isSpeaker: boolean;
}

function ParticipantsCard({ state, onMute, onUnmute, onKick }: ParticipantsCardProps) {
  // Moderators first, current speaker, talking, then alphabetical.
  const rows: ParticipantTableRow[] = useMemo(() => {
    const speakerId = state.currentSpeakerMemberId;
    return [...state.participants]
      .map((p) => ({ ...p, id: p.memberId, isSpeaker: p.memberId === speakerId }))
      .sort((a, b) => {
        if (a.isModerator !== b.isModerator) return a.isModerator ? -1 : 1;
        if (a.isSpeaker !== b.isSpeaker) return a.isSpeaker ? -1 : 1;
        if (a.isTalking !== b.isTalking) return a.isTalking ? -1 : 1;
        const an = (a.displayName ?? a.e164 ?? a.memberId).toLowerCase();
        const bn = (b.displayName ?? b.e164 ?? b.memberId).toLowerCase();
        return an.localeCompare(bn);
      });
  }, [state.participants, state.currentSpeakerMemberId]);

  const columns = [
    {
      header: 'Participant',
      cell: (r: ParticipantTableRow) => (
        <div className="flex items-center gap-2 min-w-0">
          <div className="min-w-0">
            <div className="text-sm font-medium truncate flex items-center gap-1.5">
              {r.displayName ?? r.e164 ?? r.memberId.slice(0, 8)}
              {r.isModerator && (
                <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50">
                  Moderator
                </Badge>
              )}
            </div>
            <div className="text-xs text-muted-foreground font-mono">
              {r.e164 ?? '—'}
            </div>
          </div>
        </div>
      ),
    },
    {
      header: 'Status',
      cell: (r: ParticipantTableRow) => (
        <div className="flex flex-wrap items-center gap-1">
          {r.isSpeaker && <Badge variant="default" className="text-[10px]">On air</Badge>}
          {r.isTalking ? (
            <Badge variant="default" className="text-[10px] bg-emerald-100 text-emerald-700">
              Talking
            </Badge>
          ) : null}
          {r.isMuted && (
            <Badge variant="secondary" className="text-[10px]">
              Muted
            </Badge>
          )}
          {r.handRaised && (
            <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700 bg-amber-50 inline-flex items-center gap-1">
              <Hand className="h-3 w-3" /> Raised
            </Badge>
          )}
          {!r.isTalking && !r.isMuted && !r.handRaised && !r.isSpeaker && (
            <span className="text-xs text-muted-foreground">Listening</span>
          )}
        </div>
      ),
    },
    {
      header: 'Joined',
      className: 'text-xs text-muted-foreground tabular-nums',
      cell: (r: ParticipantTableRow) => <span>{formatTime(r.joinedAt)}</span>,
    },
    {
      header: '',
      className: 'text-end w-44',
      cell: (r: ParticipantTableRow) => {
        const name = r.displayName ?? r.e164 ?? r.memberId;
        return (
          <div className="flex items-center justify-end gap-1">
            {r.isMuted ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onUnmute(r)}
                aria-label={`Unmute ${name}`}
              >
                <Mic className="h-3.5 w-3.5 me-1" /> Unmute
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onMute(r)}
                aria-label={`Mute ${name}`}
              >
                <MicOff className="h-3.5 w-3.5 me-1" /> Mute
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={() => onKick(r)}
              aria-label={`Kick ${name}`}
            >
              <UserX className="h-3.5 w-3.5" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <section aria-label="Participants">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">
          Participants
          <span className="ms-2 text-xs text-muted-foreground font-normal tabular-nums">
            ({state.participants.length})
          </span>
        </h2>
      </div>
      {rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            No one in the room yet.
          </CardContent>
        </Card>
      ) : (
        <DataTable rows={rows} columns={columns} />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Controls card
// ─────────────────────────────────────────────────────────────────────

interface ControlsCardProps {
  state: LiveConferenceState;
  onToggleRec: (next: boolean) => void;
  onToggleLock: (next: boolean) => void;
  onMuteAll: () => void;
  onUnmuteAll: () => void;
  onPlay: () => void;
  onDial: () => void;
  recordingPending: boolean;
  lockPending: boolean;
  muteAllPending: boolean;
  unmuteAllPending: boolean;
}

function ControlsCard({
  state,
  onToggleRec,
  onToggleLock,
  onMuteAll,
  onUnmuteAll,
  onPlay,
  onDial,
  recordingPending,
  lockPending,
  muteAllPending,
  unmuteAllPending,
}: ControlsCardProps) {
  const recOn = state.recordingCustomerFacing.active;
  // Compact layout (Sam 2026-05-19): the original card had a lot of
  // vertical breathing room — large rows for Recording / Lock / Q&A
  // each with sub-descriptions, then a 2×2 action grid. The dashboard
  // is a "fits on one screen" tool, so we tighten every row, drop the
  // standalone "Controls" heading (the card border + page context are
  // enough), inline the descriptions as tiny secondary text, and lay
  // the four bulk actions out as a single 4-column row on wider screens.
  return (
    <section aria-label="Controls">
      <Card>
        <CardContent className="p-3 space-y-2.5">
          {/* Recording — headline row (label + small status + button) */}
          <div className="flex items-center justify-between gap-3 py-0.5">
            <div className="min-w-0 flex items-center gap-2">
              <Disc className="h-4 w-4 text-slate-500 shrink-0" />
              <div className="min-w-0">
                <div className="text-sm font-medium leading-tight">Recording</div>
                {recOn && (
                  <div className="text-[11px] text-muted-foreground truncate">
                    {state.recordingCustomerFacing.path?.split('/').pop() ?? 'Capturing audio'}
                  </div>
                )}
              </div>
            </div>
            <Button
              variant={recOn ? 'destructive' : 'default'}
              size="sm"
              disabled={recordingPending}
              onClick={() => onToggleRec(!recOn)}
              aria-pressed={recOn}
            >
              {recOn ? (
                <>
                  <span className="inline-block h-2 w-2 rounded-full bg-white animate-pulse me-1" />
                  Stop
                </>
              ) : (
                <>
                  <Disc className="h-3.5 w-3.5 me-1" /> Start
                </>
              )}
            </Button>
          </div>

          {/* Lock + Q&A — single-line toggles with hover-tooltip for detail */}
          <label
            className="flex items-center justify-between gap-3 cursor-pointer py-0.5"
            title={state.locked ? 'No new joins allowed' : 'New callers can join'}
          >
            <span className="text-sm font-medium flex items-center gap-2 min-w-0">
              <Lock className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              Lock room
            </span>
            <Switch
              checked={state.locked}
              disabled={lockPending}
              onCheckedChange={(v) => onToggleLock(v)}
              aria-label="Lock room"
            />
          </label>

          <label
            className="flex items-center justify-between gap-3 cursor-pointer py-0.5"
            title="Listeners press *1 to raise their hand"
          >
            <span className="text-sm font-medium flex items-center gap-2 min-w-0">
              <Hand className="h-3.5 w-3.5 text-slate-500 shrink-0" />
              Q&amp;A mode
            </span>
            {/*
              Q&A mode toggle isn't wired to a single REST verb on the
              dashboard router — it's controlled by the conference room's
              `mode` configuration. We surface the state as a read-only
              indicator (disabled Switch); toggling permanently happens
              on the room edit page.
            */}
            <Switch
              checked={state.qaMode}
              disabled
              aria-label="Q&A mode (read-only)"
            />
          </label>

          {/* Bulk audio actions — single row on wider screens, 2x2 on narrow */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5 pt-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={onMuteAll}
              disabled={muteAllPending}
            >
              <VolumeX className="h-3.5 w-3.5 me-1.5" /> Mute all
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={onUnmuteAll}
              disabled={unmuteAllPending}
            >
              <Volume2 className="h-3.5 w-3.5 me-1.5" /> Unmute all
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={onPlay}
            >
              <PlayCircle className="h-3.5 w-3.5 me-1.5" /> Play prompt
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-8"
              onClick={onDial}
            >
              <PhoneOutgoing className="h-3.5 w-3.5 me-1.5" /> Dial out
            </Button>
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Q&A queue card
// ─────────────────────────────────────────────────────────────────────

interface QaQueueCardProps {
  state: LiveConferenceState;
  onCallOn: (memberId?: string) => void;
  onReorder: (ordered: string[]) => void;
  onLower: (memberId: string) => void;
}

function QaQueueCard({ state, onCallOn, onReorder, onLower }: QaQueueCardProps) {
  const speaker = state.currentSpeakerMemberId
    ? state.participants.find((p) => p.memberId === state.currentSpeakerMemberId) ?? null
    : null;
  const queue = state.qaQueue;

  function move(idx: number, delta: number) {
    const next = [...queue];
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    const [removed] = next.splice(idx, 1);
    if (removed) next.splice(target, 0, removed);
    onReorder(next.map((q) => q.memberId));
  }

  return (
    <section aria-label="Q and A queue" data-testid="qa-pane">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
          <Hand className="h-3.5 w-3.5" /> Q&amp;A queue
          <span className="text-xs text-muted-foreground font-normal tabular-nums">
            ({queue.length})
          </span>
        </h2>
      </div>
      <Card>
        <CardContent className="p-3 space-y-2">
          {speaker && (
            <div
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 flex items-center gap-2"
              data-testid="qa-speaker"
            >
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-500" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-amber-900 truncate">
                  {speaker.displayName ?? speaker.e164 ?? speaker.memberId}
                </div>
                <div className="text-[11px] text-amber-700">
                  On air · {speaker.e164 ?? 'no caller id'}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => onCallOn()}
              >
                Close &amp; next
              </Button>
            </div>
          )}

          {queue.length === 0 ? (
            <div className="text-center text-xs text-muted-foreground py-6">
              No hands raised yet.
              <div className="mt-1 text-[11px]">
                Listeners press{' '}
                <code className="rounded bg-slate-100 px-1 text-[10px]">*1</code>
                {' '}to raise ·{' '}
                <code className="rounded bg-slate-100 px-1 text-[10px]">*2</code>
                {' '}to lower
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-border" data-testid="qa-list">
              {queue.map((entry, idx) => {
                const name = entry.displayName ?? entry.e164 ?? entry.memberId;
                return (
                  <li
                    key={entry.memberId}
                    className="grid grid-cols-[1.5rem_2rem_1fr_auto] gap-2 items-center py-2"
                    data-testid={`qa-row-${entry.memberId}`}
                  >
                    <span className="text-xs font-mono tabular-nums text-muted-foreground text-center">
                      {idx + 1}
                    </span>
                    <div className="flex flex-col gap-0.5">
                      <button
                        type="button"
                        aria-label="Move up"
                        disabled={idx === 0}
                        onClick={() => move(idx, -1)}
                        className="h-4 rounded border border-border text-slate-500 hover:bg-slate-50 disabled:opacity-30 inline-grid place-items-center"
                      >
                        <ArrowUp className="h-2.5 w-2.5" />
                      </button>
                      <button
                        type="button"
                        aria-label="Move down"
                        disabled={idx === queue.length - 1}
                        onClick={() => move(idx, 1)}
                        className="h-4 rounded border border-border text-slate-500 hover:bg-slate-50 disabled:opacity-30 inline-grid place-items-center"
                      >
                        <ArrowDown className="h-2.5 w-2.5" />
                      </button>
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {entry.e164 ?? '—'} · <WaitTimer raisedAt={entry.raisedAt} />
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => onCallOn(entry.memberId)}
                        aria-label={`Call on ${name}`}
                      >
                        Call on
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-slate-500 hover:bg-slate-100 hover:text-red-600 h-9 w-9 p-0"
                        onClick={() => onLower(entry.memberId)}
                        aria-label={`Lower ${name}'s hand`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Event log card
// ─────────────────────────────────────────────────────────────────────

function EventLogCard({ log }: { log: LiveEvent[] }) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [pinnedToBottom, setPinnedToBottom] = useState(true);

  // Auto-scroll only when the user is already at the bottom — otherwise
  // the operator scrolled up to read history and shouldn't be yanked
  // back down on every new event.
  useEffect(() => {
    if (!pinnedToBottom) return;
    const el = scrollerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log.length, pinnedToBottom]);

  function onScroll() {
    const el = scrollerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 12;
    setPinnedToBottom(atBottom);
  }

  return (
    <section aria-label="Event log">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">
          Event log
          <span className="ms-2 text-xs text-muted-foreground font-normal tabular-nums">
            ({log.length})
          </span>
        </h2>
        {!pinnedToBottom && log.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setPinnedToBottom(true);
              const el = scrollerRef.current;
              if (el) el.scrollTop = el.scrollHeight;
            }}
          >
            <ArrowDown className="h-3.5 w-3.5 me-1" /> Jump to latest
          </Button>
        )}
      </div>
      <Card>
        <CardContent className="p-0">
          <div
            ref={scrollerRef}
            onScroll={onScroll}
            className="max-h-72 overflow-y-auto"
          >
            {log.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground">
                Waiting for the first event…
              </div>
            ) : (
              <ul className="divide-y divide-border">
                {log.map((e, idx) => (
                  <EventRow key={idx} event={e} />
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>
    </section>
  );
}

function EventRow({ event }: { event: LiveEvent }) {
  const time = 'at' in event ? formatTime(event.at) : '';
  const kind = labelForKind(event);
  const variant = badgeVariantForEvent(event);
  const colorClass = badgeColorClass(event);
  const msg = messageFor(event);
  return (
    <li className="flex items-center gap-3 px-4 py-2 text-xs">
      <span className="font-mono tabular-nums text-muted-foreground w-16 shrink-0">
        {time}
      </span>
      <Badge variant={variant} className={`text-[10px] uppercase tracking-wide ${colorClass}`}>
        {kind}
      </Badge>
      <span className="text-slate-700 truncate flex-1">{msg}</span>
    </li>
  );
}

function labelForKind(e: LiveEvent): string {
  switch (e.kind) {
    case 'join': return 'join';
    case 'leave': return 'leave';
    case 'mute': return e.isMuted ? 'mute' : 'unmute';
    case 'talking': return e.isTalking ? 'talk' : 'silent';
    case 'kick': return 'remove';
    case 'lock': return e.locked ? 'lock' : 'unlock';
    case 'qa_mode': return e.qaMode ? 'q&a on' : 'q&a off';
    case 'hand_raised': return 'hand+';
    case 'hand_lowered': return 'hand-';
    case 'speaker_opened': return 'open';
    case 'speaker_closed': return 'close';
    case 'queue_reordered': return 'reorder';
    case 'recording': return e.state.active ? 'rec on' : 'rec off';
    case 'dial': return 'dial';
    case 'play': return 'play';
    case 'end': return 'end';
    default: return 'event';
  }
}

function badgeVariantForEvent(e: LiveEvent): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (e.kind) {
    case 'kick':
    case 'end':
      return 'destructive';
    case 'join':
    case 'leave':
      return 'default';
    case 'mute':
    case 'talking':
      return 'secondary';
    case 'lock':
    case 'qa_mode':
    case 'hand_raised':
    case 'hand_lowered':
    case 'recording':
      return 'outline';
    default:
      return 'outline';
  }
}

function badgeColorClass(e: LiveEvent): string {
  switch (e.kind) {
    case 'recording':
      return e.state.active ? 'border-red-300 text-red-700 bg-red-50' : '';
    case 'hand_raised':
    case 'hand_lowered':
      return 'border-amber-300 text-amber-700 bg-amber-50';
    case 'lock':
      return 'border-slate-300 text-slate-700 bg-slate-50';
    case 'qa_mode':
      return 'border-purple-300 text-purple-700 bg-purple-50';
    default:
      return '';
  }
}

function messageFor(e: LiveEvent): ReactNode {
  switch (e.kind) {
    case 'join':
      return (
        <>
          {e.participant.displayName ?? e.participant.e164 ?? e.participant.memberId} joined
          {e.participant.isModerator && (
            <Badge variant="outline" className="ms-2 text-[10px] border-amber-300 text-amber-700 bg-amber-50">
              Moderator
            </Badge>
          )}
        </>
      );
    case 'leave': return `member ${e.memberId.slice(-4)} left`;
    case 'mute': return `${e.memberId.slice(-4)} ${e.isMuted ? 'muted' : 'unmuted'}`;
    case 'talking': return `${e.memberId.slice(-4)} ${e.isTalking ? 'started talking' : 'went silent'}`;
    case 'kick': return `removed ${e.memberId.slice(-4)}`;
    case 'lock': return e.locked ? 'room locked' : 'room unlocked';
    case 'qa_mode': return `q&a ${e.qaMode ? 'enabled' : 'disabled'}`;
    case 'hand_raised':
      return `${e.entry.displayName ?? e.entry.e164 ?? e.entry.memberId} raised hand`;
    case 'hand_lowered': return `${e.memberId.slice(-4)} lowered hand`;
    case 'speaker_opened': return `opened ${e.memberId.slice(-4)}`;
    case 'speaker_closed': return `closed ${e.memberId.slice(-4)}`;
    case 'queue_reordered': return `queue reordered (${e.ordered.length})`;
    case 'recording': return e.state.active ? `recording → ${e.which}` : `${e.which} stopped`;
    case 'dial': return `dial → ${e.sofiaTarget}`;
    case 'play': return `play → ${e.path.split('/').pop() ?? e.path}`;
    case 'end': return 'conference ended';
    default: return '';
  }
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour12: false });
}

// ─────────────────────────────────────────────────────────────────────
// Dialogs
// ─────────────────────────────────────────────────────────────────────

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description: string;
  confirmLabel: string;
  confirmVariant?: 'default' | 'destructive';
  pending?: boolean;
  onConfirm: () => void;
}

function ConfirmDialog({
  open, onOpenChange, title, description, confirmLabel,
  confirmVariant = 'default', pending = false, onConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-600">{description}</p>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" disabled={pending}>
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            variant={confirmVariant}
            disabled={pending}
            onClick={onConfirm}
            autoFocus
          >
            {pending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface PlayPromptDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pending: boolean;
  /** Customer scope for the recording picker. */
  customerId: string | null;
  onPlay: (input: { recordingId?: string; path?: string }) => void;
}

function PlayPromptDialog({
  open, onOpenChange, pending, customerId, onPlay,
}: PlayPromptDialogProps) {
  // The dialog offers two modes:
  //   - Pick a recording from the library (default; the backend resolves
  //     the recording_id to a local FS path via the active storage
  //     driver before issuing the FS verb).
  //   - Fall back to a raw FS path for power-users / silence streams.
  const [mode, setMode] = useState<'recording' | 'path'>('recording');
  const [recordingId, setRecordingId] = useState<string | null>(null);
  const [path, setPath] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Reset on open/close so we don't carry stale input between sessions.
  useEffect(() => {
    if (!open) {
      setMode('recording');
      setRecordingId(null);
      setPath('');
      setError(null);
    }
  }, [open]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    if (mode === 'recording') {
      if (!recordingId) {
        setError('Pick a recording from the library.');
        return;
      }
      onPlay({ recordingId });
      return;
    }
    if (!path.trim()) {
      setError('Path is required.');
      return;
    }
    onPlay({ path: path.trim() });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Play prompt into room</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex items-center gap-2 text-xs">
            <button
              type="button"
              className={
                'rounded-md border px-2 py-1 ' +
                (mode === 'recording'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:text-foreground')
              }
              onClick={() => { setMode('recording'); setError(null); }}
            >
              From recording library
            </button>
            <button
              type="button"
              className={
                'rounded-md border px-2 py-1 ' +
                (mode === 'path'
                  ? 'border-primary bg-primary/10 text-primary'
                  : 'border-border text-muted-foreground hover:text-foreground')
              }
              onClick={() => { setMode('path'); setError(null); }}
            >
              Raw file path
            </button>
          </div>

          {mode === 'recording' ? (
            <div className="space-y-1">
              <Label>Recording</Label>
              <RecordingPicker
                value={recordingId}
                onChange={setRecordingId}
                customerId={customerId}
                hideCreateMenu
              />
              <p className="text-xs text-muted-foreground">
                Plays to everyone in the room. The server resolves the
                recording to a local path before sending the command, so
                S3 and filesystem rows both work.
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              <Label htmlFor="play-path">File path or locator</Label>
              <Input
                id="play-path"
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder="/var/ivrello/recordings/.../file.wav"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                For ad-hoc / debug use only (e.g.{' '}
                <code>silence_stream://1000</code>). Most operators should
                use the recording library above.
              </p>
            </div>
          )}

          {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={pending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={pending}>
              {pending ? 'Sending…' : 'Play'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DialOutDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  pending: boolean;
  /** Customer scope for the extension + contact pickers. */
  customerId: string | null;
  onDial: (sofiaTarget: string, asModerator: boolean) => void;
}

type DialMode = 'extension' | 'contact' | 'raw';

function DialOutDialog({
  open, onOpenChange, pending, customerId, onDial,
}: DialOutDialogProps) {
  const [mode, setMode] = useState<DialMode>('extension');
  const [extensionId, setExtensionId] = useState<string | null>(null);
  const [contactId, setContactId] = useState<string | null>(null);
  const [rawTarget, setRawTarget] = useState('');
  const [asModerator, setAsModerator] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setMode('extension');
      setExtensionId(null);
      setContactId(null);
      setRawTarget('');
      setAsModerator(false);
      setError(null);
    }
  }, [open]);

  // Picker data — fetched lazily once the dialog opens. Empty array
  // fallbacks let the dropdowns render an "(none)" state without
  // throwing.
  const extensionsQ = useQuery({
    queryKey: ['extensions-picker', customerId ?? ''],
    queryFn: () => extensions.list({
      customer_id: customerId ?? undefined,
      limit: 200,
    }),
    enabled: open && mode === 'extension',
  });
  const contactsQ = useQuery({
    queryKey: ['contacts-picker', customerId ?? ''],
    queryFn: () => contacts.list({
      customer_id: customerId ?? undefined,
      active_only: true,
      limit: 200,
    }),
    enabled: open && mode === 'contact',
  });
  const extensionRows = extensionsQ.data?.rows ?? [];
  const contactRows = contactsQ.data?.rows ?? [];

  /**
   * Build the Sofia dialstring from the chosen mode.
   *
   * Convention follows the existing `dial` IVR node handler in
   * backend/src/freeswitch/engine/nodes/dial.ts:
   *   - extension      → `user/<number>` (registered SIP endpoint)
   *   - contact / E.164 → `sofia/external/<e164>` (PSTN out via the
   *     platform-default 'external' Sofia profile)
   *
   * The raw mode passes the string through verbatim, so a moderator
   * who needs a non-default gateway (`sofia/gateway/<name>/<target>`)
   * still has an escape hatch.
   */
  function buildTarget(): string | null {
    if (mode === 'extension') {
      const row = extensionRows.find((r) => r.id === extensionId);
      if (!row) return null;
      return `user/${row.number}`;
    }
    if (mode === 'contact') {
      const row = contactRows.find((r) => r.id === contactId);
      if (!row) return null;
      const e164 = row.external_phone?.trim();
      if (!e164) return null;
      return `sofia/external/${e164}`;
    }
    // raw mode
    const t = rawTarget.trim();
    if (!t) return null;
    // If the operator already prefixed sofia/ or user/, pass through;
    // otherwise wrap a bare E.164 in the external profile.
    if (t.startsWith('sofia/') || t.startsWith('user/')) return t;
    return `sofia/external/${t}`;
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    e.stopPropagation();
    const target = buildTarget();
    if (!target) {
      setError(
        mode === 'extension' ? 'Pick an extension to dial.'
        : mode === 'contact' ? 'Pick a contact with an external phone.'
        : 'Target is required.',
      );
      return;
    }
    onDial(target, asModerator);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Dial out</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <DialModeButton
              active={mode === 'extension'}
              onClick={() => { setMode('extension'); setError(null); }}
            >Extension</DialModeButton>
            <DialModeButton
              active={mode === 'contact'}
              onClick={() => { setMode('contact'); setError(null); }}
            >Contact</DialModeButton>
            <DialModeButton
              active={mode === 'raw'}
              onClick={() => { setMode('raw'); setError(null); }}
            >Raw target</DialModeButton>
          </div>

          {mode === 'extension' && (
            <div className="space-y-1">
              <Label htmlFor="dial-extension">Extension</Label>
              <select
                id="dial-extension"
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={extensionId ?? ''}
                onChange={(e) => setExtensionId(e.target.value || null)}
              >
                <option value="">
                  {extensionsQ.isLoading ? 'Loading extensions…' : 'Select an extension'}
                </option>
                {extensionRows.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.number}{r.display_name ? ` — ${r.display_name}` : ''}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Dials a local SIP endpoint via <code>user/&lt;number&gt;</code>.
              </p>
            </div>
          )}

          {mode === 'contact' && (
            <div className="space-y-1">
              <Label htmlFor="dial-contact">Contact</Label>
              <select
                id="dial-contact"
                className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                value={contactId ?? ''}
                onChange={(e) => setContactId(e.target.value || null)}
              >
                <option value="">
                  {contactsQ.isLoading ? 'Loading contacts…' : 'Select a contact'}
                </option>
                {contactRows.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.display_name}{r.external_phone ? ` — ${r.external_phone}` : ' — (no number)'}
                  </option>
                ))}
              </select>
              <p className="text-xs text-muted-foreground">
                Dials the contact&rsquo;s external phone via
                <code> sofia/external/</code>. Contacts without an external
                phone aren&rsquo;t dialable.
              </p>
            </div>
          )}

          {mode === 'raw' && (
            <div className="space-y-1">
              <Label htmlFor="dial-raw">Target</Label>
              <Input
                id="dial-raw"
                value={rawTarget}
                onChange={(e) => setRawTarget(e.target.value)}
                placeholder="+15551234567 or sofia/gateway/bw/+15551234567"
                autoFocus
              />
              <p className="text-xs text-muted-foreground">
                E.164 numbers get wrapped in <code>sofia/external/&lt;num&gt;</code>.
                Already-prefixed targets (<code>sofia/</code>, <code>user/</code>)
                pass through verbatim. Use this for non-default gateways.
              </p>
            </div>
          )}

          <label className="flex items-center gap-2 cursor-pointer">
            <Switch checked={asModerator} onCheckedChange={setAsModerator} />
            <span className="text-sm">Join as moderator</span>
          </label>
          {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" disabled={pending}>
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={pending}>
              {pending ? 'Dialing…' : 'Dial'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DialModeButton({
  active, onClick, children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      className={
        'rounded-md border px-2 py-1 ' +
        (active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:text-foreground')
      }
      onClick={onClick}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Tiny live-update helpers
// ─────────────────────────────────────────────────────────────────────

function LiveDuration({ startedAt }: { startedAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const start = new Date(startedAt).getTime();
  if (Number.isNaN(start)) return <span>—</span>;
  const seconds = Math.max(0, Math.floor((now - start) / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return (
    <span className="tabular-nums">
      {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
    </span>
  );
}

function WaitTimer({ raisedAt }: { raisedAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const raised = new Date(raisedAt).getTime();
  if (Number.isNaN(raised)) return <span>—</span>;
  const seconds = Math.max(0, Math.floor((now - raised) / 1000));
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return (
    <span className="tabular-nums">
      waiting {m}:{String(s).padStart(2, '0')}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Event application
// ─────────────────────────────────────────────────────────────────────

/**
 * Pure-ish reducer: takes the previous state + an event and returns the
 * next state. Exported so tests can verify the fold without spinning up
 * the page. `state_snapshot` events overwrite wholesale; everything else
 * is a targeted patch.
 */
export function applyEvent(
  prev: LiveConferenceState | null,
  event: LiveEvent,
): LiveConferenceState | null {
  if (event.kind === 'state_snapshot') return event.state;
  if (!prev) return prev;

  switch (event.kind) {
    case 'join': {
      const next = prev.participants.filter((p) => p.memberId !== event.participant.memberId);
      return { ...prev, participants: [...next, event.participant] };
    }
    case 'leave':
    case 'kick': {
      return {
        ...prev,
        participants: prev.participants.filter((p) => p.memberId !== event.memberId),
        qaQueue: prev.qaQueue.filter((q) => q.memberId !== event.memberId),
        currentSpeakerMemberId:
          prev.currentSpeakerMemberId === event.memberId ? null : prev.currentSpeakerMemberId,
      };
    }
    case 'mute': {
      return {
        ...prev,
        participants: prev.participants.map((p) =>
          p.memberId === event.memberId ? { ...p, isMuted: event.isMuted } : p,
        ),
      };
    }
    case 'talking': {
      return {
        ...prev,
        participants: prev.participants.map((p) =>
          p.memberId === event.memberId ? { ...p, isTalking: event.isTalking } : p,
        ),
      };
    }
    case 'lock':
      return { ...prev, locked: event.locked };
    case 'qa_mode':
      return { ...prev, qaMode: event.qaMode };
    case 'hand_raised': {
      // de-dupe + push to end
      const filtered = prev.qaQueue.filter((q) => q.memberId !== event.entry.memberId);
      const participants = prev.participants.map((p) =>
        p.memberId === event.entry.memberId ? { ...p, handRaised: true } : p,
      );
      return { ...prev, qaQueue: [...filtered, event.entry], participants };
    }
    case 'hand_lowered': {
      return {
        ...prev,
        qaQueue: prev.qaQueue.filter((q) => q.memberId !== event.memberId),
        participants: prev.participants.map((p) =>
          p.memberId === event.memberId ? { ...p, handRaised: false } : p,
        ),
      };
    }
    case 'speaker_opened': {
      return {
        ...prev,
        currentSpeakerMemberId: event.memberId,
        qaQueue: prev.qaQueue.filter((q) => q.memberId !== event.memberId),
        participants: prev.participants.map((p) =>
          p.memberId === event.memberId
            ? { ...p, isMuted: false, handRaised: false }
            : p,
        ),
      };
    }
    case 'speaker_closed': {
      return {
        ...prev,
        currentSpeakerMemberId:
          prev.currentSpeakerMemberId === event.memberId ? null : prev.currentSpeakerMemberId,
      };
    }
    case 'queue_reordered': {
      const byId = new Map(prev.qaQueue.map((q) => [q.memberId, q]));
      const ordered: QaQueueEntry[] = [];
      for (const id of event.ordered) {
        const item = byId.get(id);
        if (item) ordered.push(item);
      }
      return { ...prev, qaQueue: ordered };
    }
    case 'recording': {
      if (event.which === 'customer') {
        return { ...prev, recordingCustomerFacing: event.state };
      }
      return { ...prev, recordingBackend: event.state };
    }
    case 'dial':
    case 'play':
    case 'end':
      return prev;
    default:
      return prev;
  }
}

